// frontend/src/components/formularios/FormularioDependiente.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  TextField,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  Stack,
} from '@mui/material';
import {
  Save as SaveIcon,
  Lock as LockIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Assignment as AssignmentIcon,
  ArrowBack,
  Timeline as TimelineIcon,
  Edit as EditIcon,
  LockOpen as LockOpenIcon
} from '@mui/icons-material';
import formularioService from '../../services/formularioService';
import { useAuth } from '../../hooks/useAuth';
import SelectorObjetivosPadre from './SelectorObjetivosPadre';
import TablaEvaluacionMensual from './TablaEvaluacionMensual';
import EstadoSeguimiento from './EstadoSeguimiento';

const FormularioDependiente = () => {
  const { formularioId, historiaId, parentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Estados principales
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formulario, setFormulario] = useState(null);
  const [formularioPadre, setFormularioPadre] = useState(null);
  const [respuestas, setRespuestas] = useState({});
  const [estructura, setEstructura] = useState({ campos: [], secciones: [] });
  
  // Estados de seguimiento
  const [objetivosSeleccionados, setObjetivosSeleccionados] = useState([]);
  const [historialSeguimiento, setHistorialSeguimiento] = useState([]);
  const [bloqueado, setBloqueado] = useState(false);
  const [mensajeBloqueo, setMensajeBloqueo] = useState('');
  
  // Estados de UI
  const [alert, setAlert] = useState({ show: false, message: '', type: 'info' });
  const [dialogoAnalisis, setDialogoAnalisis] = useState(false);
  const [observacionAnalisis, setObservacionAnalisis] = useState('');
  const [mostrarProgreso, setMostrarProgreso] = useState(false);
  
  // NUEVO: Estado para controlar reapertura libre
  const [modoEdicionLibre, setModoEdicionLibre] = useState(false);
  
  useEffect(() => {
    cargarDatos();
  }, [formularioId, historiaId, parentId]);
  
  const cargarDatos = async () => {
    try {
      setLoading(true);
      
      // Cargar formulario
      const formData = await formularioService.getFormulario(formularioId);
      setFormulario(formData);
      
      // Procesar estructura
      const estructuraProcesada = typeof formData.estructura === 'string' ? 
        JSON.parse(formData.estructura) : formData.estructura;
      setEstructura(estructuraProcesada);
      
      // NUEVO: Detectar si es psicoterapia para habilitar modo libre
      const esPsicoterapia = detectarPsicoterapia(formData);
      setModoEdicionLibre(esPsicoterapia);
  
      // Si es un formulario hijo, cargar datos del padre
      if (formData.tipo_dependencia?.includes('hijo') && parentId) {
        await cargarDatosPadre(parentId);
      }
      
    } catch (error) {
      console.error('Error al cargar datos:', error);
      setAlert({
        show: true,
        message: 'Error al cargar el formulario',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };
  
  // NUEVO: Función para detectar si es psicoterapia
  const detectarPsicoterapia = (formData) => {
    const nombre = formData.nombre?.toLowerCase() || '';
    const descripcion = formData.descripcion?.toLowerCase() || '';
    const tipoDependencia = formData.tipo_dependencia?.toLowerCase() || '';
    
    // Buscar indicadores de psicoterapia
    const indicadoresPsicoterapia = [
      'psicoterapia',
      'terapia',
      'seguimiento',
      'mensual',
      'plan de trabajo',
      'objetivos especificos'
    ];
    
    const esPsicoterapia = indicadoresPsicoterapia.some(indicador => 
      nombre.includes(indicador) || 
      descripcion.includes(indicador) ||
      tipoDependencia.includes(indicador)
    );
    
    // También verificar por ID de formulario si tienes IDs específicos
    const idsPsicoterapia = [/* aquí puedes agregar IDs específicos si los conoces */];
    const esPorId = idsPsicoterapia.includes(parseInt(formularioId));
    
    return esPsicoterapia || esPorId;
  };
  
  const cargarDatosPadre = async (parentRespuestaId) => {
    try {
      // Obtener respuesta padre
      const respuestaPadre = await formularioService.getRespuesta(parentRespuestaId);
      setFormularioPadre(respuestaPadre);
      
      // MODIFICADO: Para psicoterapia, NO verificar que el padre esté cerrado
      if (!modoEdicionLibre && respuestaPadre.estado !== 'cerrado') {
        setAlert({
          show: true,
          message: 'El formulario padre debe estar cerrado antes de crear seguimientos',
          type: 'warning'
        });
        setTimeout(() => navigate(-1), 2000);
        return;
      }
      
      // Cargar objetivos disponibles
      const objetivosData = await formularioService.getObjetivosDisponibles(parentRespuestaId);      
      // Cargar historial de seguimientos previos
      const respuestasHijos = await formularioService.getRespuestasPorHistoria({
        parent_respuesta_id: parentRespuestaId
      });
      
      if (respuestasHijos.data && Array.isArray(respuestasHijos.data)) {
        const seguimientosAnteriores = respuestasHijos.data.filter(r => 
          r.parent_respuesta_id === parentRespuestaId &&
          r.formulario_id === parseInt(formularioId)
        );
        
        setHistorialSeguimiento(seguimientosAnteriores);
        
        // MODIFICADO: Para psicoterapia, NO verificar bloqueos
        if (!modoEdicionLibre) {
          verificarBloqueos(seguimientosAnteriores);
        }
      }
      
    } catch (error) {
      console.error('Error al cargar datos del padre:', error);
      setAlert({
        show: true,
        message: 'Error al cargar el plan terapéutico',
        type: 'error'
      });
    }
  };
  
  const verificarBloqueos = (seguimientos) => {
    if (!formulario?.reglas_bloqueo || seguimientos.length === 0) return;
    
    const reglas = typeof formulario.reglas_bloqueo === 'string' ? 
      JSON.parse(formulario.reglas_bloqueo) : formulario.reglas_bloqueo;
    
    // Verificar reglas según el tipo
    if (formulario.tipo_dependencia === 'hijo_cualitativo') {
      verificarBloqueoCualitativo(seguimientos, reglas);
    } else if (formulario.tipo_dependencia === 'hijo_cuantitativo') {
      verificarBloqueoCuantitativo(seguimientos, reglas);
    }
  };
  
  const verificarBloqueoCualitativo = (seguimientos, reglas) => {
    for (const regla of reglas.reglas) {
      if (regla.tipo === 'consecutivos_en_proceso') {
        const ultimosN = seguimientos.slice(-regla.valor);
        
        if (ultimosN.length === regla.valor) {
          const todosEnProceso = ultimosN.every(s => 
            s.respuestas?.estado_progreso === regla.valor_campo
          );
          
          if (todosEnProceso) {
            setBloqueado(true);
            setMensajeBloqueo(regla.mensaje);
            if (regla.requiere_observacion) {
              setDialogoAnalisis(true);
            }
            return;
          }
        }
      }
    }
  };
  
  const verificarBloqueoCuantitativo = (seguimientos, reglas) => {
    // Implementar verificación para formularios cuantitativos
    // Similar pero con lógica de puntajes
  };
  
  const handleChange = (fieldId, value) => {
    setRespuestas(prev => ({
      ...prev,
      [fieldId]: value
    }));
  };
  
  // NUEVO: Función para forzar reapertura (solo psicoterapia)
  const forzarReapertura = () => {
    if (!modoEdicionLibre) return;
    
    setBloqueado(false);
    setMensajeBloqueo('');
    setAlert({
      show: true,
      message: 'Formulario reabierto para edición libre (Psicoterapia)',
      type: 'success'
    });
  };
  
  const handleGuardar = async () => {
    try {
      setSaving(true);
      
      // Validar que se haya seleccionado un objetivo si es requerido (solo para no-psicoterapia)
      if (!modoEdicionLibre && 
          formulario.tipo_dependencia === 'hijo_cualitativo' && 
          !respuestas.objetivo_seleccionado) {
        setAlert({
          show: true,
          message: 'Debe seleccionar un objetivo específico',
          type: 'warning'
        });
        return;
      }
      
      // Preparar datos
      const datosGuardar = {
        formulario_id: parseInt(formularioId),
        historia_especialidad_id: parseInt(historiaId),
        parent_respuesta_id: parentId ? parseInt(parentId) : null,
        paciente_id: formularioPadre?.paciente_id,
        especialista_id: user?.id,
        respuestas,
        fecha_completado: new Date().toISOString().split('T')[0],
        estado: 'abierto' // MODIFICADO: Para psicoterapia, siempre abierto
      };
      
      // Si es formulario hijo, agregar objetivos seleccionados
      if (formulario.tipo_dependencia?.includes('hijo')) {
        datosGuardar.objetivos_seleccionados = respuestas.objetivo_seleccionado ? 
          [respuestas.objetivo_seleccionado] : [];
        datosGuardar.datos_referencia = {
          formulario_padre_id: formularioPadre?.formulario_id,
          fecha_plan: formularioPadre?.fecha_completado
        };
      }
      
      // Guardar usando el método apropiado
      let resultado;
      if (formulario.tipo_dependencia?.includes('hijo')) {
        resultado = await formularioService.guardarRespuestaHijo(datosGuardar);
      } else {
        resultado = await formularioService.guardarRespuesta(datosGuardar);
      }
      
      setAlert({
        show: true,
        message: modoEdicionLibre ? 
          'Formulario guardado (Psicoterapia - Edición libre)' : 
          'Formulario guardado correctamente',
        type: 'success'
      });
      
      // Para psicoterapia, NO redirigir automáticamente
      if (!modoEdicionLibre) {
        setTimeout(() => {
          navigate(`/historias/${historiaId}`);
        }, 1500);
      }
      
    } catch (error) {
      console.error('Error al guardar:', error);
      setAlert({
        show: true,
        message: error.response?.data?.message || 'Error al guardar el formulario',
        type: 'error'
      });
    } finally {
      setSaving(false);
    }
  };
  
  const handleGuardarAnalisis = async () => {
    try {
      // Guardar el análisis y desbloquear
      const datosAnalisis = {
        formulario_respuesta_id: historialSeguimiento[historialSeguimiento.length - 1].id,
        observacion: observacionAnalisis,
        accion: 'analisis_bloqueo',
        usuario_id: user?.id
      };
      
      setDialogoAnalisis(false);
      setAlert({
        show: true,
        message: 'Análisis guardado. Contacte al administrador para desbloquear.',
        type: 'info'
      });
      
    } catch (error) {
      console.error('Error al guardar análisis:', error);
    }
  };
  
  const renderCampo = (campo) => {
    // Para selector de objetivos del padre
    if (campo.tipo === 'seleccion_objetivo_padre') {
      return (
        <SelectorObjetivosPadre
          key={campo.id}
          formularioPadre={formularioPadre}
          objetivosSeleccionados={objetivosSeleccionados}
          onSeleccionarObjetivo={(objId) => {
            handleChange('objetivo_seleccionado', objId);
            setObjetivosSeleccionados([...objetivosSeleccionados, objId]);
          }}
          configuracion={campo.configuracion}
        />
      );
    }
    
    // Para tabla de evaluación mensual
    if (campo.tipo === 'tabla_evaluacion_mensual') {
      return (
        <TablaEvaluacionMensual
          key={campo.id}
          campo={campo}
          formularioPadre={formularioPadre}
          value={respuestas[campo.id] || {}}
          onChange={(value) => handleChange(campo.id, value)}
        />
      );
    }
    
    // Renderizar otros campos normalmente
    switch (campo.tipo) {
      case 'texto_largo':
        return (
          <TextField
            key={campo.id}
            fullWidth
            multiline
            rows={campo.filas || 4}
            label={campo.etiqueta}
            placeholder={campo.placeholder}
            value={respuestas[campo.id] || ''}
            onChange={(e) => handleChange(campo.id, e.target.value)}
            required={campo.requerido}
            margin="normal"
            disabled={!modoEdicionLibre && bloqueado} // MODIFICADO: Para psicoterapia, nunca deshabilitado
          />
        );
        
      case 'fecha':
        return (
          <TextField
            key={campo.id}
            fullWidth
            type="date"
            label={campo.etiqueta}
            value={respuestas[campo.id] || ''}
            onChange={(e) => handleChange(campo.id, e.target.value)}
            InputLabelProps={{ shrink: true }}
            required={campo.requerido}
            margin="normal"
            disabled={!modoEdicionLibre && bloqueado} // MODIFICADO
          />
        );
        
      case 'seleccion':
        return (
          <FormControl key={campo.id} fullWidth margin="normal">
            <InputLabel>{campo.etiqueta}</InputLabel>
            <Select
              value={respuestas[campo.id] || ''}
              onChange={(e) => handleChange(campo.id, e.target.value)}
              label={campo.etiqueta}
              required={campo.requerido}
              disabled={!modoEdicionLibre && bloqueado} // MODIFICADO
            >
              {campo.opciones?.map(opcion => (
                <MenuItem key={opcion.valor} value={opcion.valor}>
                  {opcion.etiqueta}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
        
      default:
        return (
          <TextField
            key={campo.id}
            fullWidth
            label={campo.etiqueta}
            placeholder={campo.placeholder}
            value={respuestas[campo.id] || ''}
            onChange={(e) => handleChange(campo.id, e.target.value)}
            required={campo.requerido}
            margin="normal"
            disabled={!modoEdicionLibre && bloqueado} // MODIFICADO
          />
        );
    }
  };
  
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
        <CircularProgress />
      </Box>
    );
  }
  
  // MODIFICADO: Para psicoterapia, nunca mostrar pantalla de bloqueo
  if (!modoEdicionLibre && bloqueado) {
    return (
      <Box p={3}>
        <Paper sx={{ p: 3 }}>
          <Alert severity="error" icon={<LockIcon />}>
            <Typography variant="h6">{mensajeBloqueo}</Typography>
          </Alert>
          
          <Box mt={3}>
            <Button 
              variant="outlined" 
              startIcon={<ArrowBack />}
              onClick={() => navigate(`/historias/${historiaId}`)}
            >
              Volver a la historia
            </Button>
          </Box>
        </Paper>
        
        {/* Diálogo de análisis */}
        <Dialog 
          open={dialogoAnalisis} 
          onClose={() => setDialogoAnalisis(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Análisis Requerido</DialogTitle>
          <DialogContent>
            <Typography paragraph>
              {mensajeBloqueo}
            </Typography>
            <Typography paragraph variant="body2" color="textSecondary">
              Por favor, proporcione un análisis detallado de la situación y las 
              medidas a tomar.
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={6}
              label="Análisis y observaciones"
              value={observacionAnalisis}
              onChange={(e) => setObservacionAnalisis(e.target.value)}
              margin="normal"
              placeholder="Describa el análisis de la situación..."
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogoAnalisis(false)}>
              Cancelar
            </Button>
            <Button 
              variant="contained" 
              onClick={handleGuardarAnalisis}
              disabled={!observacionAnalisis.trim()}
            >
              Guardar Análisis
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Alertas */}
      {alert.show && (
        <Alert 
          message={alert.message} 
          type={alert.type}
          onClose={() => setAlert({ ...alert, show: false })}
        />
      )}
      
      {/* Encabezado */}
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <Button
              startIcon={<ArrowBack />}
              onClick={() => navigate(`/historias/${historiaId}`)}
            >
              Volver
            </Button>
            <Typography variant="h4">
              {formulario?.nombre}
            </Typography>
            
            {/* NUEVO: Indicador de modo libre */}
            {modoEdicionLibre && (
              <Chip 
                icon={<LockOpenIcon />}
                label="Edición Libre - Psicoterapia"
                color="success"
                variant="outlined"
              />
            )}
          </Box>
          
          <Stack direction="row" spacing={1}>
            {historialSeguimiento.length > 0 && (
              <Tooltip title="Ver progreso">
                <IconButton
                  color="primary"
                  onClick={() => setMostrarProgreso(!mostrarProgreso)}
                >
                  <TimelineIcon />
                </IconButton>
              </Tooltip>
            )}
            
            {/* NUEVO: Botón de reapertura para psicoterapia */}
            {modoEdicionLibre && bloqueado && (
              <Tooltip title="Forzar reapertura (Psicoterapia)">
                <IconButton
                  color="success"
                  onClick={forzarReapertura}
                >
                  <LockOpenIcon />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Box>
        
        {formulario?.descripcion && (
          <Typography variant="body1" color="textSecondary">
            {formulario.descripcion}
          </Typography>
        )}
        
        {/* NUEVO: Mensaje informativo para psicoterapia */}
        {modoEdicionLibre && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Modo Psicoterapia:</strong> Este formulario puede ser reabierto y editado en cualquier momento. 
              No hay restricciones de bloqueo para la especialidad de psicoterapia.
            </Typography>
          </Alert>
        )}
      </Paper>
      
      {/* Información del plan padre si existe */}
      {formularioPadre && (
        <Card variant="outlined" sx={{ mb: 3, bgcolor: 'grey.50' }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="subtitle2" color="textSecondary">
                  Plan Terapéutico Base:
                </Typography>
                <Typography variant="h6">
                  {formularioPadre.formulario?.nombre}
                </Typography>
              </Box>
              <Chip 
                icon={<AssignmentIcon />}
                label={modoEdicionLibre ? 
                  `Disponible (${new Date(formularioPadre.fecha_completado).toLocaleDateString()})` :
                  `Cerrado el ${new Date(formularioPadre.fecha_completado).toLocaleDateString()}`
                }
                color={modoEdicionLibre ? "info" : "success"}
                variant="outlined"
              />
            </Box>
          </CardContent>
        </Card>
      )}
      
      {/* Estado del seguimiento */}
      {formulario?.tipo_dependencia?.includes('hijo') && !modoEdicionLibre && (
        <EstadoSeguimiento 
          historialSeguimiento={historialSeguimiento}
          reglasBloqueo={formulario.reglas_bloqueo}
          mostrarDetalle={mostrarProgreso}
        />
      )}
      
      {/* Formulario */}
      <Paper elevation={3} sx={{ p: 3 }}>
        <form onSubmit={(e) => { e.preventDefault(); handleGuardar(); }}>
          {/* Renderizar secciones si existen */}
          {estructura.secciones?.length > 0 ? (
            estructura.secciones.map(seccion => (
              <Box key={seccion.id} mb={4}>
                <Typography variant="h6" gutterBottom>
                  {seccion.titulo}
                </Typography>
                {seccion.descripcion && (
                  <Typography variant="body2" color="textSecondary" paragraph>
                    {seccion.descripcion}
                  </Typography>
                )}
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  {estructura.campos
                    .filter(campo => campo.seccion_id === seccion.id)
                    .sort((a, b) => (a.orden || 0) - (b.orden || 0))
                    .map(campo => (
                      <Grid 
                        item 
                        xs={12} 
                        md={campo.tipo === 'texto_largo' || 
                            campo.tipo.includes('tabla') ? 12 : 6}
                        key={campo.id}
                      >
                        {renderCampo(campo)}
                      </Grid>
                    ))}
                </Grid>
              </Box>
            ))
          ) : (
            // Si no hay secciones, renderizar todos los campos
            <Grid container spacing={2}>
              {estructura.campos?.map(campo => (
                <Grid 
                  item 
                  xs={12} 
                  md={campo.tipo === 'texto_largo' || 
                      campo.tipo.includes('tabla') ? 12 : 6}
                  key={campo.id}
                >
                  {renderCampo(campo)}
                </Grid>
              ))}
            </Grid>
          )}
          
          {/* Botones de acción */}
          <Box mt={3} display="flex" justifyContent="space-between">
            <Button 
              variant="outlined" 
              onClick={() => navigate(`/historias/${historiaId}`)}
            >
              {modoEdicionLibre ? 'Volver' : 'Cancelar'}
            </Button>
            
            <Button
              type="submit"
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              disabled={saving || (!modoEdicionLibre && bloqueado)}
            >
              {saving ? 'Guardando...' : 
               modoEdicionLibre ? 'Guardar (Edición Libre)' : 'Guardar Formulario'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default FormularioDependiente;